import {BehaviorSubject, combineLatest, ReplaySubject, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {langUtil} from '@px/helper/lang';
import {div, gt, max, numUtil, times, toFixed, toString} from '@px/helper/num';
import {timeUtil} from '@px/helper/time';
import {coinApi} from '@px/api/coin';
import {currenciesCache$} from './config';
import {indexPricesCache$} from './index-price';
import {url$} from './url';
import {lang$} from './lang';

interface CoinInfo {
  currency: string; // 币种名称
  logoUrl: string; // logo链
  rank: number; // 排
  circulatingSupply: number; // 流通量
  maximumSupply: number; // 最大供给量
  totalSupply: number; // 总量
  launchingDate: string; // 发行日期
  launchingPrice: string; // 发行价
  blockExplorerUrl: string; // 区块查询链接
  websiteUrl: string; // 官网链接
  academyUrl: string; // Academy链
  ccPurchasable: number; // 是否可以信用买币
  description: string; // 币种介绍
  currencyCode: number;
  displayCurrency: string;
  inAssetsDisplay: number;
  fullName: string;
  ath: string;
  athDate: string;
  atl: string;
  atlDate: string;
  category: string;
  marketCap: string;
  sourceCodeUrl: string;
  circulationRate: number;
  officialWebsiteUrl: string;
  gitHubUrl: string;
  chainName: string;
  contractAddress: string;
  shortContractAddress: string;
  fullyDilutedValuation: string;
}

const innerCoinInfo$ = new BehaviorSubject(null);
export const coinInfo$ = new BehaviorSubject<CoinInfo>(null);

export const loadPilotCoinsChain$ = new Subject<void>();
export const pilotCoinsChainCache$ = new ReplaySubject<Map<string, any>>(1);

loadPilotCoinsChain$
  .pipe(
    withLatestFrom(currenciesCache$),
    switchMap(([_, currenciesCache]) => getPilotCoins(currenciesCache)),
    filter(Boolean),
    take(1),
    map((o) => o.reduce((p, item) => p.set(item.currency, item), new Map()))
  )
  .subscribe(pilotCoinsChainCache$);

const langOb$ = new BehaviorSubject(null);
const currency$ = new BehaviorSubject('');

lang$.pipe(distinctUntilChanged()).subscribe((lang) => langOb$.next(lang));
url$
  .pipe(
    filter((o) => o && !!o.tradePair),
    map((o) => o.tradePair.baseCurrency),
    distinctUntilChanged()
  )
  .subscribe((currency) => currency$.next(currency));

combineLatest([currency$, currenciesCache$, langOb$])
  .pipe(switchMap(([currency, currenciesCache, langOb]) => loadCoinInfo(currency, currenciesCache, langOb)))
  .subscribe(innerCoinInfo$);

combineLatest([innerCoinInfo$, currenciesCache$, indexPricesCache$])
  .pipe(
    map(([coinInfo, currenciesCache, indexPricesCache]) => {
      if (!coinInfo) {
        return coinInfo;
      }
      const {currency} = coinInfo;
      const currencyInfo = currenciesCache.has(currency) ? currenciesCache.get(currency) : {displayCurrency: currency};

      const circulationRatio = gt(coinInfo.totalSupply || 0, 0) ? div(coinInfo.circulatingSupply || 0, coinInfo.totalSupply) : 0;
      const circulationRatio2 = toFixed(times(circulationRatio, 100), 2);
      const indexPrice = genIndexPrice(currency, indexPricesCache);
      return {
        ...coinInfo,
        shortContractAddress: convertShortAddress(coinInfo.contractAddress),
        displayCurrency: currencyInfo.displayCurrency,
        marketCap: coinInfo.marketCap ? numUtil.delimit(numUtil.floor(coinInfo.marketCap, 0)) : coinInfo.marketCap,
        maximumSupply: coinInfo.maximumSupply ? numUtil.delimit(numUtil.floor(coinInfo.maximumSupply, 0)) : coinInfo.maximumSupply,
        circulatingSupply: coinInfo.circulatingSupply ? numUtil.delimit(numUtil.floor(coinInfo.circulatingSupply, 0)) : '--',
        totalSupply: coinInfo.totalSupply ? numUtil.delimit(numUtil.floor(coinInfo.totalSupply, 0)) : '--',
        launchingPrice: coinInfo.launchingPrice ? '$' + coinInfo.launchingPrice : '--',
        ath: coinInfo.ath ? '$' + toString(coinInfo.ath) : '--',
        atl: coinInfo.atl ? '$' + toString(coinInfo.atl) : '--',
        circulationRate: max(circulationRatio2, 0.01) + '%',
        athDate: coinInfo.athDate ? timeUtil.formatDate(coinInfo.athDate) : '--',
        atlDate: coinInfo.athDate ? timeUtil.formatDate(coinInfo.atlDate) : '--',
        fullyDilutedValuation: coinInfo.totalSupply ? numUtil.delimit(numUtil.floor(times(coinInfo.totalSupply, indexPrice), 0)) : '--',
      };
    })
  )
  .subscribe(coinInfo$);

export function createDefaultCoinInfo() {
  const currency = currency$.getValue();
  return {
    currency,
    logoUrl: '',
    rank: 0,
    circulatingSupply: 0,
    maximumSupply: 0,
    totalSupply: 0,
    launchingDate: '',
    launchingPrice: '',
    blockExplorerUrl: '',
    websiteUrl: '',
    academyUrl: '',
    ccPurchasable: 0,
    description: '',
    currencyCode: 0,
    displayCurrency: currency,
    inAssetsDisplay: 0,
    fullName: '',
    ath: '',
    athDate: '',
    atl: '',
    atlDate: '',
    category: '',
    marketCap: '',
    sourceCodeUrl: '',
    circulationRate: 0,
    officialWebsiteUrl: '',
    gitHubUrl: '',
    fullyDilutedValuation: '',
  };
}

async function loadCoinInfo(currency, currenciesCache, lang) {
  if (!currency || !lang) {
    return null;
  }

  const currency2 = convertCurrency(currency, currenciesCache);
  const {data} = await coinApi.coinInfo({currency: currency2, lang: langUtil.getLanguageKey(lang)});
  return data || null;
}

async function getPilotCoins(currenciesCache) {
  const {data} = await coinApi.getPilotCoins();
  if (data) {
    return data.map((o) => {
      const info = currenciesCache.has(o.currency) ? currenciesCache.get(o.currency) : {displayCurrency: o.currency};
      return {
        ...o,
        displayCurrency: info.displayCurrency,
        contractAddress: o.contractAddress || '',
        shortAddress: convertShortAddress(o.contractAddress),
        chainName: o.chainName || '',
        marketCapEv: o.marketCap,
        marketCap: numUtil.abbreviateNumber(Math.floor(o.marketCap)),
      };
    });
  }
  return data;
}

export function convertCurrency(currency, currenciesCache) {
  if (currenciesCache.has(currency)) {
    return currency;
  }
  let currency2 = currency.replace(/^10+/, '');
  if (currenciesCache.has(currency2)) {
    return currency2;
  }
  currency2 = currency.replace(/[0-9]/g, '');
  if (currenciesCache.has(currency2)) {
    return currency2;
  }
  return currency;
}

export function convertShortAddress(address: string) {
  if (!address) {
    return '';
  }
  return address.substring(0, 4) + '....' + address.substring(address.length - 4);
}

function genIndexPrice(currency, map) {
  if (!currency || !map) {
    return 0;
  }
  const item = map.get(`.${currency}`);
  return item?.price || 0;
}
